import React, { ReactNode, useRef, useEffect } from 'react';
import FocusLock from 'react-focus-lock';
import classNames from 'classnames';
import Button from '../Button';
import Icon from '../Icon';
import { BASE_CLASS_NAME, OVERLAY_CLASS_NAME } from './constants';

export interface BottomSheetProps {
  id?: string;
  className?: string;
  isOpen: boolean;
  hasOverlay?: boolean;
  hasCloseBtn?: boolean;
  onClose?: () => void;
  theme?: 'light' | 'dark';
  children: ReactNode;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  id,
  className,
  isOpen,
  hasOverlay = true,
  hasCloseBtn = false,
  onClose = () => {},
  theme = 'light',
  children,
}: BottomSheetProps) => {
  const shouldUseAnimation = useRef<boolean>(false);

  useEffect(() => {
    if (isOpen && !shouldUseAnimation.current) {
      shouldUseAnimation.current = true;
    }
  }, [isOpen]);

  const bottomSheetClass: string = classNames(
    className,
    BASE_CLASS_NAME,
    `${BASE_CLASS_NAME}--${theme}`,
    {
      [`${BASE_CLASS_NAME}--visible-animated`]: isOpen,
      [`${BASE_CLASS_NAME}--hidden`]: !isOpen && !shouldUseAnimation.current,
      [`${BASE_CLASS_NAME}--hidden-animated`]:
        !isOpen && shouldUseAnimation.current,
    }
  );

  const overlayClass: string = classNames(
    className && `${className}-overlay`,
    OVERLAY_CLASS_NAME,
    `${OVERLAY_CLASS_NAME}--${isOpen ? 'visible' : 'hidden'}`
  );

  return (
    <>
      {hasOverlay && (
        <div className={overlayClass} role="presentation" onClick={onClose} />
      )}
      <FocusLock returnFocus>
        <div id={id} className={bottomSheetClass} role="alertdialog">
          {hasCloseBtn && (
            <Button
              className={`${BASE_CLASS_NAME}__close`}
              iconOnly={true}
              onClick={onClose}
              color="none"
              size="medium"
            >
              <Icon
                className={`${BASE_CLASS_NAME}__close-icon`}
                name="close"
                size="x-small"
              />
            </Button>
          )}

          <div className={`${BASE_CLASS_NAME}__content`}>{children}</div>
        </div>
      </FocusLock>
    </>
  );
};

export default BottomSheet;
